import { useEffect, useState, useRef } from 'react';
import * as Yup from 'yup';
import { BlobServiceClient } from '@azure/storage-blob';
import { toast } from 'react-toastify';
import { Ic } from '../../shared/component/GosIcon';

/* Add / Update User — GrowthOS modal (matched to growthos/Users.jsx EditUserModal + InviteModal).
   The CHROME was rebuilt from the react-bootstrap + Formik markup to the shared .gos-import-* shell +
   a .gos-eu block (avatar upload, 2-col field grid, read-only Auth Type on edit, L1/L2/L3 access-level
   segment). ALL real logic is unchanged: the Yup validationSchema, handleChangeForm (phone formatting +
   email/phone inline validation), handleChange/handleLookupDropDownChange (auth_type/accessLevel — auth
   change clears the opposite password/oid fields), handleSubmit (validate → strip mask → drop azure/db
   fields → handleFormSubmit), and the Azure-blob image upload (handleUploadImage → saveUploadImage).
   Add mode shows Auth Type (select) + Azure Object Id (azure) / Password + Confirm (db); edit mode locks
   Auth Type (read-only) and hides the password fields — same conditions the legacy form used. */

const validationSchema = Yup.object().shape({
  empId: Yup.string()
    .required('Employee ID is required')
    .min(1, 'Employee ID must be at least 1 character')
    .max(10, 'Employee ID must be less than 10 characters long'),

  firstName: Yup.string().required('First Name is required'),
  lastName: Yup.string().required('Last Name is required'),

  userName: Yup.string()
    .required('User Name is required')
    .test(
      'is-valid-email',
      'Invalid UserName format',
      (value) => !value || /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)
    ),

  phoneNumber: Yup.string().required('Phone Number is required'),

  email: Yup.string().email('Invalid email format').required('Email is required'),

  accessLevel: Yup.string().required('Access Level is required'),

  auth_type: Yup.string().when('type', {
    is: (type) => type === 'add',
    then: (schema) => schema.required('Auth Type is required'),
    otherwise: (schema) => schema.notRequired(),
  }),

  oid: Yup.string().when(['auth_type'], {
    is: (auth_type) => auth_type === 'azure',
    then: (schema) => schema.required('Azure Object Id is required'),
    otherwise: (schema) => schema.notRequired(),
  }),

  password: Yup.string().when(['auth_type', 'type'], {
    is: (auth_type, type) => auth_type === 'db' && type === 'add',
    then: (schema) => schema.required('Password is required'),
    otherwise: (schema) => schema.notRequired(),
  }),

  confirmPassword: Yup.string().when(['auth_type', 'type'], {
    is: (auth_type, type) => auth_type === 'db' && type === 'add',
    then: (schema) =>
      schema.required('Confirm Password is required').oneOf([Yup.ref('password')], 'Passwords must match'),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const AVA_COLORS = ['#c96342', '#2e63a6', '#6a4fae', '#2c7a45', '#b34480', '#9a751c', '#4f6b8f', '#0e8c6f', '#2f7ea3', '#a35a2f'];
const hashIdx = (s) => {
  const str = String(s || '');
  let h = 0;
  for (let i = 0; i < str.length; i += 1) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h % AVA_COLORS.length;
};
const initialsOf = (name) =>
  String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase() || '?';

const AUTH_TYPE_OPTIONS = [
  { id: 'azure', label: 'Azure' },
  { id: 'db', label: 'DB' },
];
const ACCESS_LEVELS = ['L1', 'L2', 'L3'];

const AddUserForm = ({ handleCloseAddForm, open, handleFormSubmit, userData, userProfileFlag }) => {
  const [formData, setFormData] = useState(userData);
  const [errormsg, setErrorMsg] = useState({});
  const [formEdited, setFormEdited] = useState(false);
  const [imagefile, setImageFile] = useState(null);
  const [imageUploadStatus, setImageUploadStatus] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const fileRef = useRef(null);

  const isEdit = formData?.type === 'edit';

  const handleLookupDropDownChange = (selectedValue, name) => {
    if (name === 'auth_type') {
      setFormData((prev) => ({
        ...prev,
        auth_type: selectedValue,
        ...(selectedValue === 'azure' && { password: '', confirmPassword: '' }),
        ...(selectedValue === 'db' && { oid: '' }),
      }));
    } else if (name === 'accessLevel') {
      setFormData((prev) => ({ ...prev, accessLevel: selectedValue }));
    }
    setFormEdited(true);
  };

  const handleChangeForm = (e) => {
    const { name, value } = e.target;
    setErrorMsg((prev) => ({ ...prev, [name]: undefined }));
    let convertedValue = value;
    if (name === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        setErrorMsg((prev) => ({ ...prev, [name]: 'Invalid email format' }));
      }
      convertedValue = value;
    } else if (name === 'phoneNumber') {
      const input = value.replace(/\D/g, '');
      if (!input && value.trim() === '') {
        setErrorMsg((prev) => ({ ...prev, [name]: 'Phone number is required' }));
      } else if (input.length < 10) {
        setErrorMsg((prev) => ({ ...prev, [name]: 'Phone number must be at least 10 digits' }));
      } else {
        setErrorMsg((prev) => ({ ...prev, [name]: undefined }));
        convertedValue = formatPhoneNumber(input);
      }
    }
    setFormData((prev) => ({ ...prev, [name]: convertedValue }));
    setFormEdited(true);
  };

  const formatPhoneNumber = (input) => {
    const areaCode = input.slice(0, 3);
    const prefix = input.slice(3, 6);
    const lineNumber = input.slice(6, 10);
    let formattedNumber = '';
    if (areaCode) formattedNumber += `(${areaCode}`;
    if (prefix) formattedNumber += `) ${prefix}`;
    if (lineNumber) formattedNumber += ` - ${lineNumber}`;
    return formattedNumber;
  };

  const handlePhoneNumberKeyPress = (e) => {
    const pattern = /[0-9]/;
    const inputChar = String.fromCharCode(e.charCode);
    if (!pattern.test(inputChar)) e.preventDefault();
  };

  const handleSubmit = async (data) => {
    if (imagefile && data?.userName) {
      const newFile = new File([imagefile], data?.userName, { type: imagefile.type });
      saveUploadImage(newFile);
    }
    const phoneNumberWithoutMask = data?.phoneNumber?.replace(/\D/g, '');
    const updatedFormData = { ...data, phoneNumber: phoneNumberWithoutMask };
    if (updatedFormData.auth_type === 'azure') {
      delete updatedFormData.password;
      delete updatedFormData.confirmPassword;
    }
    if (updatedFormData.auth_type === 'db') {
      delete updatedFormData.oid;
    }
    try {
      await validationSchema.validate(updatedFormData, { abortEarly: false });
      const {
        extension,
        family_name,
        preferred_username,
        roles,
        secondaryEmail,
        given_name,
        confirmPassword,
        ...cleanedFormData
      } = updatedFormData;
      handleFormSubmit(cleanedFormData);
    } catch (error) {
      const fieldErrors = {};
      error?.inner?.forEach((err) => {
        fieldErrors[err.path] = err.message;
      });
      setErrorMsg(fieldErrors);
      console.error('Validation Error:', fieldErrors);
    }
  };

  const handleUploadImage = (file) => {
    setImageFile(file);
    setAvatarPreview(URL.createObjectURL(file));
    setFormEdited(true);
  };

  const saveUploadImage = async (file) => {
    const sasUrl = process.env.REACT_APP_AZURE_STORAGE_SAS_URL;
    const blobServiceClient = new BlobServiceClient(sasUrl);
    const containerName = process.env.REACT_APP_AZURE_STORAGE_CONTAINER_NAME;
    const containerClient = blobServiceClient.getContainerClient(containerName);
    try {
      const blockBlobClient = containerClient.getBlockBlobClient(file.name);
      await blockBlobClient.uploadBrowserData(file);
      toast.success('Profile Image Uploaded successfully!', { position: 'top-right' });
      setImageUploadStatus(true);
    } catch (error) {
      setImageUploadStatus(false);
    }
  };

  const onPickImage = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) handleUploadImage(file);
  };

  if (!open) return null;

  const displayName =
    formData?.userName ||
    `${formData?.firstName || ''} ${formData?.lastName || ''}`.trim() ||
    formData?.email ||
    'User';
  const title = userProfileFlag ? 'User Profile Details' : isEdit ? 'Update User' : 'Add User';
  const isDb = formData?.auth_type === 'db';
  const isAzure = formData?.auth_type === 'azure';
  const saveDisabled = isEdit && !formEdited;

  return (
    <div
      className="gos-import-overlay gos-eu"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) handleCloseAddForm();
      }}
    >
      <div className="gos-import-modal" style={{ maxWidth: 600 }} onMouseDown={(e) => e.stopPropagation()}>
        <div className="gos-import-head">
          <div className="gos-import-head-l">
            {isEdit ? (
              <span className="gos-eu-headava" style={{ background: AVA_COLORS[hashIdx(displayName)] }}>
                {initialsOf(displayName)}
              </span>
            ) : (
              <span className="gos-import-step-ic">
                <Ic name="user" size={18} />
              </span>
            )}
            <div>
              <div className="gos-import-title">{title}</div>
              <div className="gos-import-sub">
                {isEdit ? formData?.email : 'Create a new workspace account.'}
              </div>
            </div>
          </div>
          <button type="button" className="gos-import-close" onClick={handleCloseAddForm}>
            <Ic name="x" size={18} />
          </button>
        </div>

        <div className="gos-import-body">
          {/* avatar upload */}
          <div className="gos-eu-avatar">
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onPickImage} />
            <button
              type="button"
              className="gos-eu-avatar-btn"
              onClick={() => fileRef.current && fileRef.current.click()}
              disabled={userProfileFlag}
            >
              {avatarPreview ? (
                <img className="gos-eu-avatar-img" src={avatarPreview} alt="" />
              ) : (
                <span className="gos-eu-avatar-fallback" style={{ background: AVA_COLORS[hashIdx(displayName)] }}>
                  {initialsOf(displayName)}
                </span>
              )}
              <span className="gos-eu-avatar-ov">
                <Ic name="camera" size={18} />
              </span>
            </button>
            <span className="gos-eu-avatar-badge">
              <Ic name="arrowUp" size={13} />
            </span>
          </div>
          <div className="gos-eu-avatar-cap">Click to upload a photo · JPG or PNG</div>

          <div className="gos-eu-grid">
            <div className="gos-eu-field">
              <label className="gos-tm-flabel">Employee ID<span className="gos-eu-req"> *</span></label>
              <input
                className={'gos-tm-input' + (errormsg.empId ? ' err' : '')}
                name="empId"
                value={formData?.empId || ''}
                onChange={handleChangeForm}
                disabled={userProfileFlag}
              />
              {errormsg.empId ? <div className="gos-eu-err">{errormsg.empId}</div> : null}
            </div>

            <div className="gos-eu-field">
              <label className="gos-tm-flabel">First Name<span className="gos-eu-req"> *</span></label>
              <input
                className={'gos-tm-input' + (errormsg.firstName ? ' err' : '')}
                name="firstName"
                value={formData?.firstName || ''}
                onChange={handleChangeForm}
                disabled={userProfileFlag}
              />
              {errormsg.firstName ? <div className="gos-eu-err">{errormsg.firstName}</div> : null}
            </div>

            <div className="gos-eu-field">
              <label className="gos-tm-flabel">Last Name<span className="gos-eu-req"> *</span></label>
              <input
                className={'gos-tm-input' + (errormsg.lastName ? ' err' : '')}
                name="lastName"
                value={formData?.lastName || ''}
                onChange={handleChangeForm}
                disabled={userProfileFlag}
              />
              {errormsg.lastName ? <div className="gos-eu-err">{errormsg.lastName}</div> : null}
            </div>

            <div className="gos-eu-field">
              <label className="gos-tm-flabel">Phone Number<span className="gos-eu-req"> *</span></label>
              <input
                className={'gos-tm-input' + (errormsg.phoneNumber ? ' err' : '')}
                name="phoneNumber"
                value={formData?.phoneNumber || ''}
                onChange={handleChangeForm}
                onKeyPress={handlePhoneNumberKeyPress}
                placeholder="(###) ### - ####"
                disabled={userProfileFlag}
              />
              {errormsg.phoneNumber ? <div className="gos-eu-err">{errormsg.phoneNumber}</div> : null}
            </div>

            <div className="gos-eu-field">
              <label className="gos-tm-flabel">User Name (Email)<span className="gos-eu-req"> *</span></label>
              <input
                className={'gos-tm-input' + (errormsg.userName ? ' err' : '')}
                name="userName"
                value={formData?.userName || ''}
                onChange={handleChangeForm}
                placeholder="example@company.com"
                disabled={userProfileFlag}
              />
              {errormsg.userName ? <div className="gos-eu-err">{errormsg.userName}</div> : null}
            </div>

            <div className="gos-eu-field">
              <label className="gos-tm-flabel">Email<span className="gos-eu-req"> *</span></label>
              <input
                className={'gos-tm-input' + (errormsg.email ? ' err' : '')}
                name="email"
                value={formData?.email || ''}
                onChange={handleChangeForm}
                placeholder="example@company.com"
                disabled={userProfileFlag}
              />
              {errormsg.email ? <div className="gos-eu-err">{errormsg.email}</div> : null}
            </div>

            {/* Auth type — read-only on edit (matches legacy disabled SelectBox), select on add */}
            <div className="gos-eu-field">
              <label className="gos-tm-flabel">Auth Type{!isEdit ? <span className="gos-eu-req"> *</span> : null}</label>
              {isEdit ? (
                <div className="gos-eu-readonly">
                  <Ic name="lock" size={13} />
                  {(formData?.auth_type || 'db').toUpperCase()}
                </div>
              ) : (
                <div className="gos-eu-native-wrap">
                  <select
                    className="gos-eu-native"
                    value={formData?.auth_type || ''}
                    onChange={(e) => handleLookupDropDownChange(e.target.value, 'auth_type')}
                  >
                    <option value="" disabled>
                      Select Auth Type
                    </option>
                    {AUTH_TYPE_OPTIONS.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <Ic name="chevD" size={15} className="gos-eu-native-caret" />
                </div>
              )}
              {errormsg.auth_type ? <div className="gos-eu-err">{errormsg.auth_type}</div> : null}
            </div>

            {/* Access level segment */}
            <div className="gos-eu-field">
              <label className="gos-tm-flabel">Access Level<span className="gos-eu-req"> *</span></label>
              <div className="gos-eu-seg">
                {ACCESS_LEVELS.map((l) => (
                  <button
                    type="button"
                    key={l}
                    className={'gos-eu-seg-b' + ((formData?.accessLevel || '') === l ? ' on' : '')}
                    onClick={() => !userProfileFlag && handleLookupDropDownChange(l, 'accessLevel')}
                    disabled={userProfileFlag}
                  >
                    {l}
                  </button>
                ))}
              </div>
              {errormsg.accessLevel ? <div className="gos-eu-err">{errormsg.accessLevel}</div> : null}
            </div>

            {/* Azure Object Id (add + azure) */}
            {!isEdit && isAzure && (
              <div className="gos-eu-field">
                <label className="gos-tm-flabel">Azure Object Id<span className="gos-eu-req"> *</span></label>
                <input
                  className={'gos-tm-input' + (errormsg.oid ? ' err' : '')}
                  name="oid"
                  value={formData?.oid || ''}
                  onChange={handleChangeForm}
                />
                {errormsg.oid ? <div className="gos-eu-err">{errormsg.oid}</div> : null}
              </div>
            )}

            {/* Password + confirm (add + db) */}
            {!isEdit && isDb && (
              <>
                <div className="gos-eu-field">
                  <label className="gos-tm-flabel">Password<span className="gos-eu-req"> *</span></label>
                  <div className="gos-eu-pw">
                    <input
                      className={'gos-tm-input' + (errormsg.password ? ' err' : '')}
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData?.password || ''}
                      onChange={handleChangeForm}
                      placeholder="Enter Password"
                      autoComplete="new-password"
                    />
                    <button type="button" className="gos-eu-eye" onClick={() => setShowPassword((s) => !s)}>
                      <Ic name={showPassword ? 'eyeOff' : 'eye'} size={16} />
                    </button>
                  </div>
                  {errormsg.password ? <div className="gos-eu-err">{errormsg.password}</div> : null}
                </div>

                <div className="gos-eu-field">
                  <label className="gos-tm-flabel">Confirm Password<span className="gos-eu-req"> *</span></label>
                  <div className="gos-eu-pw">
                    <input
                      className={'gos-tm-input' + (errormsg.confirmPassword ? ' err' : '')}
                      type={showConfirm ? 'text' : 'password'}
                      name="confirmPassword"
                      value={formData?.confirmPassword || ''}
                      onChange={handleChangeForm}
                      placeholder="Confirm Password"
                      autoComplete="new-password"
                    />
                    <button type="button" className="gos-eu-eye" onClick={() => setShowConfirm((s) => !s)}>
                      <Ic name={showConfirm ? 'eyeOff' : 'eye'} size={16} />
                    </button>
                  </div>
                  {errormsg.confirmPassword ? <div className="gos-eu-err">{errormsg.confirmPassword}</div> : null}
                </div>
              </>
            )}
          </div>
        </div>

        {!userProfileFlag && (
          <div className="gos-import-foot end">
            <div className="gos-foot-actions">
              <button type="button" className="gos-btn gos-btn-ghost" onClick={handleCloseAddForm}>
                Cancel
              </button>
              <button
                type="button"
                className="gos-btn gos-btn-primary"
                onClick={() => handleSubmit(formData)}
                disabled={saveDisabled}
              >
                <Ic name="check" size={16} />
                Save
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddUserForm;
